import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Grid,
    IconButton,
    Tooltip,
    useTheme
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../services/api';

const MetricCard = ({ title, value, subtitle }) => {
    const theme = useTheme();
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    {title}
                </Typography>
                <Typography variant="h4" component="div" sx={{ mb: 1 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" color="textSecondary">
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

const TMSMetrics = () => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const theme = useTheme();

    const fetchMetrics = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/monitoring/tms-metrics');
            setMetrics(response.data.data);
            setError(null);
        } catch (error) {
            setError('Failed to load TMS metrics');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMetrics();
        // Refresh metrics every minute
        const interval = setInterval(fetchMetrics, 60000);
        return () => clearInterval(interval);
    }, []);

    if (loading && !metrics) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">TMS Token Metrics</Typography>
                <Tooltip title="Refresh metrics">
                    <IconButton onClick={fetchMetrics} disabled={loading}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="Cache Hit Ratio"
                        value={`${(parseFloat(metrics?.cacheHitRatio || 0) * 100).toFixed(1)}%`}
                        subtitle={`${metrics?.cacheHits || 0} hits / ${metrics?.tokenRequests || 0} requests`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="Token Status"
                        value={metrics?.currentTokenValid ? 'Valid' : 'Invalid'}
                        subtitle={metrics?.currentTokenValid ? 
                            `Expires in ${Math.round(metrics.timeToExpiry / 1000 / 60)} minutes` : 
                            'No valid token'
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="Error Rate"
                        value={`${metrics?.errors || 0}`}
                        subtitle={metrics?.lastError ? 
                            `Last error: ${new Date(metrics.lastError.timestamp).toLocaleString()}` :
                            'No errors'
                        }
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="Requests per Hour"
                        value={metrics?.tokenRequestsPerHour ? 
                            metrics.tokenRequestsPerHour.toFixed(1) : 
                            '0'
                        }
                        subtitle="Average token requests per hour"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="API Calls Saved"
                        value={metrics?.cacheHits || 0}
                        subtitle="Total cached responses"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        title="Last Used"
                        value={metrics?.lastUsed ? 
                            new Date(metrics.lastUsed).toLocaleTimeString() :
                            'Never'
                        }
                        subtitle={metrics?.lastUsed ? 
                            new Date(metrics.lastUsed).toLocaleDateString() :
                            'Token not used yet'
                        }
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default TMSMetrics; 